import { useState, useEffect, useRef, useCallback } from 'react';

interface UsePollingOptions<T> {
  fetcher: () => Promise<T>;
  interval: number;
  enabled?: boolean;
  pauseOnHidden?: boolean;
}

interface UsePollingReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function usePolling<T>(options: UsePollingOptions<T>): UsePollingReturn<T> {
  const { fetcher, interval, enabled = true, pauseOnHidden = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const doFetch = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading((prev) => (!prev ? true : prev));
    try {
      const result = await fetcherRef.current();
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    doFetch();

    intervalRef.current = window.setInterval(doFetch, interval);

    const handleVisibility = () => {
      if (!pauseOnHidden) return;
      if (document.visibilityState === 'hidden') {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        doFetch();
        intervalRef.current = window.setInterval(doFetch, interval);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, interval, doFetch, pauseOnHidden]);

  return { data, isLoading, error, refetch: doFetch };
}
